import { DashboardsRequestMessageSchema, Message, CollectionRegistry } from '../types';
import { getDashboardManager } from '../dashboards/dashboard-storage';
import { logger } from '../utils/logger';

/**
 * Handle unified dashboards management request
 * Supports operations: create, update, delete, getAll, getOne
 * Only accepts requests from 'admin' type
 *
 * Strategy: Try database collections first, fall back to file-based storage if failed
 */
export async function handleDashboardsRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  // Helper function to execute collection operations
  const executeCollection = async (collection: string, op: string, params: any): Promise<any> => {
    const handler = collections[collection]?.[op];
    if (!handler) {
      return null; // Collection not found, will fall back to file storage
    }
    return await handler(params);
  };

  try {
    const request = DashboardsRequestMessageSchema.parse(data);
    const { id, payload, from } = request;
    const { operation, data: requestData } = payload;
    const dashboardId = requestData?.dashboardId;
    const dashboard = requestData?.dashboard;
    const projectId = requestData?.projectId;
    const name = requestData?.name;
    const description = requestData?.description;
    const published = requestData?.published;
    const createdBy = requestData?.createdBy;
    const updatedBy = requestData?.updatedBy;
    const allowedUsers = requestData?.allowedUsers as string[] | undefined;
    const numericId = requestData?.id;
    // Query operation fields
    const filters = requestData?.filters;
    const limit = requestData?.limit;
    const sort = requestData?.sort;

    // Read-only operations allowed for all client types (runtime, admin, etc.)
    const readOnlyOperations = ['query', 'getAll', 'getOne'];

    // Write operations require admin
    if (!readOnlyOperations.includes(operation) && from.type !== 'admin') {
      sendResponse(id, {
        success: false,
        error: 'Unauthorized: Only admin can manage dashboards'
      }, sendMessage, from.id);
      logger.warn(`Unauthorized dashboard write attempt from: ${from.type}`);
      return;
    }

    const dashboardManager = getDashboardManager();

    // Route to appropriate operation handler
    switch (operation) {
      case 'create':
        await handleCreate(id, dashboardId, dashboard, projectId, name, description, published, createdBy, allowedUsers, executeCollection, dashboardManager, sendMessage, from.id);
        break;

      case 'update':
        await handleUpdate(id, numericId, dashboardId, dashboard, name, description, published, updatedBy, allowedUsers, executeCollection, dashboardManager, sendMessage, from.id);
        break;

      case 'delete':
        await handleDelete(id, numericId, dashboardId, executeCollection, dashboardManager, sendMessage, from.id);
        break;

      case 'getAll':
        await handleGetAll(id, executeCollection, dashboardManager, sendMessage, from.id);
        break;

      case 'getOne':
        await handleGetOne(id, numericId, dashboardId, executeCollection, dashboardManager, sendMessage, from.id);
        break;

      case 'query':
        await handleQuery(id, filters, limit, sort, executeCollection, dashboardManager, sendMessage, from.id);
        break;

      default:
        sendResponse(id, {
          success: false,
          error: `Unknown operation: ${operation}`
        }, sendMessage, from.id);
    }

  } catch (error) {
    logger.error('Failed to handle dashboards request:', error);
    sendResponse(null, {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }, sendMessage);
  }
}

/**
 * Handle create dashboard operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleCreate(
  id: string,
  dashboardId: string | undefined,
  dashboard: any,
  projectId: string | undefined,
  name: string | undefined,
  description: string | undefined,
  published: boolean | undefined,
  createdBy: string | undefined,
  allowedUsers: string[] | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  dashboardManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!dashboardId || dashboardId.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Dashboard ID is required and cannot be empty'
    }, sendMessage, clientId);
    return;
  }

  if (!projectId) {
    sendResponse(id, {
      success: false,
      error: 'Project ID is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first
  try {
    const result = await executeCollection('dashboards', 'create', {
      dashboardId,
      projectId,
      name: name || '',
      description,
      dashboard,
      published,
      createdBy,
      allowedUsers
    });

    if (result && result.success) {
      logger.info(`[DB] Dashboard created successfully, ID: ${result.data?.id}`);
      sendResponse(id, {
        success: true,
        data: {
          id: result.data?.id,
          dashboardId: result.data?.dashboardId,
          dashboard: result.data?.dashboard || dashboard,
          message: `Dashboard created successfully (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to create dashboard, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage
  try {
    if (!dashboard) {
      dashboard = {};
    }

    const createdDashboard = dashboardManager.createDashboard(dashboardId, dashboard);

    logger.info(`[FILE] Dashboard '${dashboardId}' created successfully`);
    sendResponse(id, {
      success: true,
      data: {
        dashboardId,
        dashboard: createdDashboard,
        message: `Dashboard '${dashboardId}' created successfully (File)`
      }
    }, sendMessage, clientId);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create dashboard'
    }, sendMessage, clientId);
  }
}

/**
 * Handle update dashboard operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleUpdate(
  id: string,
  numericId: number | undefined,
  dashboardId: string | undefined,
  dashboard: any,
  name: string | undefined,
  description: string | undefined,
  published: boolean | undefined,
  updatedBy: string | undefined,
  allowedUsers: string[] | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  dashboardManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!numericId) {
    sendResponse(id, {
      success: false,
      error: 'Dashboard ID is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first
  try {
    const result = await executeCollection('dashboards', 'update', {
      id: numericId,
      name,
      description,
      dashboard,
      published,
      updatedBy,
      allowedUsers
    });

    if (result && result.success) {
      logger.info(`[DB] Dashboard updated successfully, ID: ${numericId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: numericId,
          dashboardId: result.data?.dashboardId,
          dashboard: result.data?.dashboard || dashboard,
          message: `Dashboard updated successfully (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to update dashboard, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage - use dashboardId if exists, otherwise stringify numericId
  const fileStorageId = dashboardId || String(numericId);
  try {
    const updatedDashboard = dashboardManager.updateDashboard(fileStorageId, dashboard);

    if (!updatedDashboard) {
      sendResponse(id, {
        success: false,
        error: `Dashboard '${fileStorageId}' not found`
      }, sendMessage, clientId);
      return;
    }

    logger.info(`[FILE] Dashboard '${fileStorageId}' updated successfully`);
    sendResponse(id, {
      success: true,
      data: {
        id: numericId,
        dashboardId: fileStorageId,
        dashboard: updatedDashboard,
        message: `Dashboard '${fileStorageId}' updated successfully (File)`
      }
    }, sendMessage, clientId);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update dashboard'
    }, sendMessage, clientId);
  }
}

/**
 * Handle delete dashboard operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleDelete(
  id: string,
  numericId: number | undefined,
  dashboardId: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  dashboardManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!numericId) {
    sendResponse(id, {
      success: false,
      error: 'Dashboard ID is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first
  try {
    const result = await executeCollection('dashboards', 'delete', { id: numericId });

    if (result && result.success) {
      logger.info(`[DB] Dashboard deleted successfully, ID: ${numericId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: numericId,
          message: `Dashboard deleted successfully (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to delete dashboard, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage - use dashboardId if exists, otherwise stringify numericId
  const fileStorageId = dashboardId || String(numericId);
  const deleted = dashboardManager.deleteDashboard(fileStorageId);

  if (!deleted) {
    sendResponse(id, {
      success: false,
      error: `Dashboard '${fileStorageId}' not found`
    }, sendMessage, clientId);
    return;
  }

  logger.info(`[FILE] Dashboard '${fileStorageId}' deleted successfully`);
  sendResponse(id, {
    success: true,
    data: {
      id: numericId,
      dashboardId: fileStorageId,
      message: `Dashboard '${fileStorageId}' deleted successfully (File)`
    }
  }, sendMessage, clientId);
}

/**
 * Handle get all dashboards operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleGetAll(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  dashboardManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Try database collection first
  try {
    const result = await executeCollection('dashboards', 'getAll', {});

    if (result && result.success) {
      logger.info(`[DB] Retrieved ${result.count} dashboards`);
      sendResponse(id, {
        success: true,
        data: {
          dashboards: result.data,
          count: result.count,
          message: `Retrieved ${result.count} dashboards (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to get all dashboards, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage
  const dashboards = dashboardManager.getAllDashboards();

  logger.info(`[FILE] Retrieved ${dashboards.length} dashboards`);
  sendResponse(id, {
    success: true,
    data: {
      dashboards,
      count: dashboards.length,
      message: `Retrieved ${dashboards.length} dashboards (File)`
    }
  }, sendMessage, clientId);
}

/**
 * Handle get one dashboard operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleGetOne(
  id: string,
  numericId: number | undefined,
  dashboardId: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  dashboardManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!numericId) {
    sendResponse(id, {
      success: false,
      error: 'Dashboard ID is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first
  try {
    const result = await executeCollection('dashboards', 'getOne', { id: numericId });

    if (result && result.success) {
      logger.info(`[DB] Retrieved dashboard ID: ${numericId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: numericId,
          dashboardId: result.data?.dashboardId,
          dashboard: result.data?.dashboard || result.data,
          message: `Retrieved dashboard (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to get dashboard, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage - use dashboardId if exists, otherwise stringify numericId
  const fileStorageId = dashboardId || String(numericId);
  const dashboard = dashboardManager.getDashboard(fileStorageId);

  if (!dashboard) {
    sendResponse(id, {
      success: false,
      error: `Dashboard '${fileStorageId}' not found`
    }, sendMessage, clientId);
    return;
  }

  logger.info(`[FILE] Retrieved dashboard: ${fileStorageId}`);
  sendResponse(id, {
    success: true,
    data: {
      id: numericId,
      dashboardId: fileStorageId,
      dashboard,
      message: `Retrieved dashboard '${fileStorageId}' (File)`
    }
  }, sendMessage, clientId);
}

/**
 * Handle query dashboards operation with filters
 * Database only - no file fallback for query operations
 */
async function handleQuery(
  id: string,
  filters: any,
  limit: number | undefined,
  sort: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  dashboardManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Try database collection
  try {
    const result = await executeCollection('dashboards', 'query', {
      filters: filters || {},
      limit,
      sort
    });

    if (result && result.success) {
      logger.info(`[DB] Query returned ${result.count} dashboards`);
      sendResponse(id, {
        success: true,
        data: {
          dashboards: result.data,
          count: result.count,
          message: `Query returned ${result.count} dashboards (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to query dashboards: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage (get all and return)
  const dashboards = dashboardManager.getAllDashboards();

  logger.info(`[FILE] Retrieved ${dashboards.length} dashboards (all - no query filter)`);
  sendResponse(id, {
    success: true,
    data: {
      dashboards,
      count: dashboards.length,
      message: `Retrieved ${dashboards.length} dashboards (File - no query filter)`
    }
  }, sendMessage, clientId);
}

/**
 * Send dashboards response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'DASHBOARDS_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'admin',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  sendMessage(response);
}
